import { observeMemberRestSessionExpiry } from '../session/session-expiry.js';
/**
 * AttachmentChips — the conversation's attachments, as the prototype's chips
 * (055 US1).
 *
 * ── Why these are conversation-level and not inside each message ────────────
 *
 * The prototype renders attachment chips INSIDE the message they arrived with.
 * Oma+ cannot: `CaseAttachmentResponse` (IS v1.6.0) carries `id`, `fileName`,
 * `contentType`, `size`, `scanStatus` and `createdAt` — and no `messageId`.
 * Grouping by timestamp proximity would assert a message association the back
 * office never stated, and would be silently wrong whenever a member uploads
 * after replying. That is the shape Constitution P VII forbids, so attachments
 * stay at conversation level (D-229(a)). D-231 tracks the upstream request that
 * would let this move into the message blocks unchanged.
 *
 * ── Why the download is a fetch and not an <a download> ─────────────────────
 *
 * Carried over unchanged from `CaseDetail` (038 FR-015). The BFF may REFUSE the
 * file when its security scan has not cleared (409); a bare link would navigate
 * the member to a raw JSON error page. So we fetch (the same-origin cookie
 * authenticates), and:
 *   200 → save the blob
 *   409 → the honest "being checked" state
 *   404/503/other → an honest "download failed"
 *
 * Do not replace this with a plain link because it looks simpler.
 */

import { type JSX, useRef, useState } from 'react';

import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { invalidateSession } from '../session/sessionInvalidation.js';
import type { CaseAttachmentView } from './CasesReaderPort.js';
import { formatBytes } from './formatBytes.js';

const noteClasses = 'text-sm text-muted-foreground';
const chipClasses =
  'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md border border-border/70 ' +
  'bg-muted/40 px-2 py-1 text-left transition hover:bg-muted focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

/** Paperclip, matching the prototype's chip affordance. Decorative — the label carries the meaning. */
function PaperclipIcon({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

type DownloadState = 'idle' | 'loading' | 'being-checked' | 'failed';

function AttachmentChip({
  caseId,
  attachment,
}: {
  readonly caseId: string;
  readonly attachment: CaseAttachmentView;
}): JSX.Element {
  const t = useTranslate();
  const { active } = useLanguage();
  const [state, setState] = useState<DownloadState>('idle');
  const inFlightRef = useRef(false);
  // 055 (owner decision 2026-09-30): one shared, LOCALISED formatter — Finnish
  // reads "180 kt", not "180 kB". Previously this file had its own formatter and
  // the compose screen had another; they disagreed.
  const size = formatBytes(attachment.size, active, {
    b: t('compose.wizard.attachments.unit.bytes'),
    kb: t('compose.wizard.attachments.unit.kilobytes'),
    mb: t('compose.wizard.attachments.unit.megabytes'),
  });

  const onDownload = async (): Promise<void> => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setState('loading');
    try {
      const res = await fetch(
        `/member/cases/${encodeURIComponent(caseId)}/attachments/${encodeURIComponent(attachment.id)}`,
        { credentials: 'same-origin' },
      );
      await observeMemberRestSessionExpiry(res);
      if (res.ok) {
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = attachment.fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, 0);
        setState('idle');
        return;
      }
      if (res.status === 401) invalidateSession();
      setState(res.status === 409 ? 'being-checked' : 'failed');
    } catch {
      setState('failed');
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <li>
      {/* The whole chip is the download control — the prototype's chip is a
          static div, but ours performs an action, so it must be a button. */}
      <button
        type="button"
        className={chipClasses}
        onClick={() => {
          void onDownload();
        }}
        aria-busy={state === 'loading'}
        // Name starts with the VISIBLE text (WCAG 2.5.3 Label in Name) and keeps
        // the size sighted users see; the action word follows.
        aria-label={[attachment.fileName, size, t('myCases.detail.download')]
          .filter((part) => part !== null)
          .join(', ')}
      >
        <PaperclipIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span
          className="min-w-0 truncate text-xs font-medium text-foreground"
          title={attachment.fileName}
        >
          {attachment.fileName}
        </span>
        {size !== null ? (
          <span className="ml-1.5 shrink-0 whitespace-nowrap text-xs text-muted-foreground">
            {size}
          </span>
        ) : null}
      </button>
      <div role="status" aria-live="polite">
        {state === 'being-checked' ? (
          <p className={`${noteClasses} mt-1`}>{t('myCases.detail.attachmentBeingChecked')}</p>
        ) : null}
        {state === 'failed' ? (
          <p className={`${noteClasses} mt-1`}>{t('myCases.detail.downloadFailed')}</p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The conversation's attachment chips.
 *
 * CALLER CONTRACT: `attachments` is non-empty. `CaseDetail` renders this and its
 * heading only when there is something to show (055, owner decision 2026-09-30),
 * so there is deliberately no empty branch here — an "Ei liitteitä." line would
 * be unreachable, and the key backing it was removed with it.
 */
export function AttachmentChips({
  caseId,
  attachments,
}: {
  readonly caseId: string;
  /** Non-empty — see the caller contract above. */
  readonly attachments: ReadonlyArray<CaseAttachmentView>;
}): JSX.Element {
  return (
    <ul className="mt-2.5 flex flex-col items-start gap-1.5">
      {attachments.map((a) => (
        <AttachmentChip key={a.id} caseId={caseId} attachment={a} />
      ))}
    </ul>
  );
}
